import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { BackButton } from '../components/ui/back-button';
import { Users, Crown, FileText, BarChart3, Search, Inbox } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { membersAPI, assemblyNotesAPI, statsAPI, contactAPI, tablesAPI, getUploadUrl } from '../services/api';
import { toast } from 'sonner';

// Import dashboard tab components
import { MembersTab, BureauTab, AssemblyTab, StatsTab, SearchTab, ContactTab } from '../components/dashboard';

const Dashboard = () => {
  const [allMembers, setAllMembers] = useState([]); // Store all members for client-side filtering
  const [bureauMembers, setBureauMembers] = useState([]);
  const [assemblyNotes, setAssemblyNotes] = useState([]);
  const [stats, setStats] = useState(null);
  const [unreadContacts, setUnreadContacts] = useState(0);
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('members');
  const { user, isAdmin, isSuperAdmin } = useAuth();

  // Single useEffect for initial data load - NO dependencies to prevent re-fetching
  useEffect(() => {
    loadData();
  }, []);

  // Unread contact messages badge (admins only)
  useEffect(() => {
    if (user && ['admin', 'superadmin'].includes(user.role)) {
      contactAPI.getUnreadCount().then((r) => setUnreadContacts(r.count)).catch(() => {});
    }
  }, [user]);

  // Load stats only when stats tab is accessed and stats are not yet loaded
  useEffect(() => {
    if (activeTab === 'stats' && user && ['admin', 'superadmin'].includes(user.role) && !stats) {
      loadStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const loadStats = async () => {
    try {
      const statsData = await statsAPI.get();
      setStats(statsData);
    } catch (e) {
      console.error('Error loading stats:', e);
      toast.error('Erreur lors du chargement des statistiques');
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const adminUser = user && ['admin', 'superadmin'].includes(user.role);
      const [membersData, bureauData, tablesData, notesData] = await Promise.all([
        membersAPI.getAll(),
        membersAPI.getBureau(),
        tablesAPI.getAll(),
        adminUser ? assemblyNotesAPI.getAll() : Promise.resolve([])
      ]);
      setAllMembers(membersData);
      setBureauMembers(bureauData);
      setTables(tablesData);
      setAssemblyNotes(notesData);
    } catch (error) {
      toast.error('Erreur lors du chargement');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Client-side filtering - no API call needed
  const filteredMembers = roleFilter === 'all' 
    ? allMembers 
    : allMembers.filter(member => member.role === roleFilter);

  // Reload members from API (used after role updates)
  const reloadMembers = async () => {
    try {
      const membersData = await membersAPI.getAll();
      setAllMembers(membersData);
    } catch (error) {
      console.error(error);
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await membersAPI.updateRole(userId, newRole);
      toast.success('Rôle mis à jour !');
      reloadMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const handleUpdateBureauRole = async (userId, bureauRole) => {
    try {
      await membersAPI.updateBureauRole(userId, bureauRole);
      toast.success('Rôle bureau mis à jour !');
      // Reload both members and bureau since bureau role affects both lists
      const [membersData, bureauData] = await Promise.all([
        membersAPI.getAll(),
        membersAPI.getBureau()
      ]);
      setAllMembers(membersData);
      setBureauMembers(bureauData);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const handleUpdatePlayerStatus = async (userId, playerStatus) => {
    try {
      await membersAPI.updatePlayerStatus(userId, playerStatus);
      toast.success('Statut joueur mis à jour !');
      reloadMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const handleUpdateTables = async (userId, newTables) => {
    try {
      const res = await membersAPI.updateTables(userId, newTables);
      setAllMembers((prev) => prev.map((m) => (m.user_id === userId ? { ...m, tables: res.tables } : m)));
      toast.success('Tables mises à jour !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const handleDeleteMember = async (userId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce membre ?')) return;
    try {
      await membersAPI.delete(userId);
      toast.success('Membre supprimé !');
      reloadMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const getAvatarUrl = (avatar) => {
    if (avatar && avatar.startsWith('data:')) return avatar;
    return getUploadUrl(avatar);
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'superadmin': return 'bg-yellow-600/80 text-white border-yellow-400/50';
      case 'admin': return 'bg-red-600/80 text-white border-red-400/50';
      case 'member': return 'bg-green-600/80 text-white border-green-400/50';
      default: return 'bg-gray-600/80 text-white border-gray-400/50';
    }
  };

  const getBureauRoleLabel = (role) => {
    const labels = {
      'president': 'Président(e)',
      'vice_president': 'Vice-Président(e)',
      'secretary': 'Secrétaire',
      'treasurer': 'Trésorier(e)',
      'member': 'Membre du bureau'
    };
    return labels[role] || role;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900/20 via-black to-black pt-24 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900/20 via-black to-black pt-20 sm:pt-24 px-2 sm:px-4 pb-12">
      <div className="container mx-auto max-w-7xl">
        <BackButton />
        
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-4xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-purple-600 tracking-wide">
            Espace Membres
          </h1>
          <p className="text-gray-400 mt-2 text-sm sm:text-base">
            Bienvenue {user?.name} ! Gérez les membres et consultez les informations de l&apos;association.
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-black/50 border border-purple-500/30 p-1 flex flex-wrap gap-1">
            <TabsTrigger 
              value="members"
              className="data-[state=active]:bg-purple-600 data-[state=active]:text-white text-xs sm:text-sm"
            >
              <Users className="mr-1 sm:mr-2 h-4 w-4" />
              <span className="hidden sm:inline">Membres</span>
              <span className="sm:hidden">Mbr</span>
            </TabsTrigger>
            <TabsTrigger 
              value="bureau"
              className="data-[state=active]:bg-purple-600 data-[state=active]:text-white text-xs sm:text-sm"
            >
              <Crown className="mr-1 sm:mr-2 h-4 w-4" />
              Bureau
            </TabsTrigger>
            {isAdmin() && (
              <TabsTrigger 
                value="assembly"
                className="data-[state=active]:bg-purple-600 data-[state=active]:text-white text-xs sm:text-sm"
              >
                <FileText className="mr-1 sm:mr-2 h-4 w-4" />
                <span className="hidden sm:inline">Registre Officiel</span>
                <span className="sm:hidden">Registre</span>
              </TabsTrigger>
            )}
            <TabsTrigger 
              value="search"
              data-testid="dashboard-tab-search"
              className="data-[state=active]:bg-purple-600 data-[state=active]:text-white text-xs sm:text-sm"
            >
              <Search className="mr-1 sm:mr-2 h-4 w-4" />
              <span className="hidden sm:inline">Recherche</span>
              <span className="sm:hidden">Rech.</span>
            </TabsTrigger>
            {isAdmin() && (
              <TabsTrigger 
                value="stats"
                data-testid="dashboard-tab-stats"
                className="data-[state=active]:bg-purple-600 data-[state=active]:text-white text-xs sm:text-sm"
              >
                <BarChart3 className="mr-1 sm:mr-2 h-4 w-4" />
                <span className="hidden sm:inline">Statistiques</span>
                <span className="sm:hidden">Stats</span>
              </TabsTrigger>
            )}
            {isAdmin() && (
              <TabsTrigger 
                value="contact"
                data-testid="dashboard-tab-contact"
                className="data-[state=active]:bg-purple-600 data-[state=active]:text-white text-xs sm:text-sm relative"
              >
                <Inbox className="mr-1 sm:mr-2 h-4 w-4" />
                <span className="hidden sm:inline">Contact</span>
                <span className="sm:hidden">Msg</span>
                {unreadContacts > 0 && (
                  <span data-testid="contact-unread-badge" className="ml-1.5 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full bg-pink-600 text-white text-[10px] font-bold">
                    {unreadContacts}
                  </span>
                )}
              </TabsTrigger>
            )}
          </TabsList>

          {/* Members Tab */}
          <TabsContent value="members">
            <MembersTab
              members={filteredMembers}
              roleFilter={roleFilter}
              setRoleFilter={setRoleFilter}
              isAdmin={isAdmin}
              isSuperAdmin={isSuperAdmin}
              getAvatarUrl={getAvatarUrl}
              handleUpdateRole={handleUpdateRole}
              handleUpdateBureauRole={handleUpdateBureauRole}
              handleUpdatePlayerStatus={handleUpdatePlayerStatus}
              handleDeleteMember={handleDeleteMember}
              getRoleBadgeClass={getRoleBadgeClass}
              tables={tables}
              setTables={setTables}
              handleUpdateTables={handleUpdateTables}
            />
          </TabsContent>

          {/* Bureau Tab */}
          <TabsContent value="bureau">
            <BureauTab
              bureauMembers={bureauMembers}
              getAvatarUrl={getAvatarUrl}
              getBureauRoleLabel={getBureauRoleLabel}
            />
          </TabsContent>

          {/* Assembly/Registre Tab (admin only: API is admin-restricted) */}
          {isAdmin() && (
            <TabsContent value="assembly">
              <AssemblyTab
                assemblyNotes={assemblyNotes}
                setAssemblyNotes={setAssemblyNotes}
                isAdmin={isAdmin}
              />
            </TabsContent>
          )}

          {/* Search Tab */}
          <TabsContent value="search">
            <SearchTab />
          </TabsContent>

          {/* Stats Tab */}
          {isAdmin() && (
            <TabsContent value="stats">
              <StatsTab stats={stats} />
            </TabsContent>
          )}

          {/* Contact messages Tab */}
          {isAdmin() && (
            <TabsContent value="contact">
              <ContactTab onUnreadChange={setUnreadContacts} />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
};

export default Dashboard;
